import { ListBody, listSpec, useRememberListUrl } from '@bemor/web-kit';
import {
    AsOfBadge,
    DateTime,
    type ListColumnDef,
    PageHeader,
} from '@bemor/web-kit/ui';
import { useNavigate } from 'react-router-dom';

import type { Policy } from '@/api/types';
import { useList } from '@/lib/queries';

export const POLICY_NAMES: Record<Policy['kind'], string> = {
    terms: 'Terms of service',
    privacy: 'Privacy policy',
    merchant_agreement: 'Merchant agreement',
    refund_policy: 'Default refund policy',
};

const columns: ListColumnDef<Policy>[] = [
    {
        key: 'kind',
        label: 'Policy',
        cell: (p) => POLICY_NAMES[p.kind],
        locked: 'start',
    },
    {
        key: 'version',
        label: 'Current version',
        cell: (p) =>
            p.current_version === 0 ? 'unpublished' : `v${p.current_version}`,
    },
    {
        key: 'effective',
        label: 'Effective from',
        kind: 'datetime',
        cell: (p) => (
            <DateTime
                value={
                    p.versions.find((v) => v.version === p.current_version)
                        ?.effective_from
                }
                format="date"
                empty="—"
            />
        ),
        noTruncate: true,
    },
    {
        key: 'draft',
        label: 'Draft',
        cell: (p) =>
            p.draft_text.length > 0
                ? `${p.draft_text.length} characters`
                : 'empty',
    },
];

/**
 * The reads of `/policies` (BM-46 §2.0). The tenant's four texts: the endpoint declares no list contract (no sorts or
 * filters), so the page is a plain table without views.
 */
export const policiesReads = () => ({
    list: listSpec('/staff/policies'),
});

export function PoliciesPage() {
    // BM-46 §2.3: a detail page's crumb returns here with these filters.
    useRememberListUrl();
    const navigate = useNavigate();
    const query = useList<Policy>(policiesReads().list.path);
    return (
        <div>
            <PageHeader
                title="Policies"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                "The tenant's four texts. Edit the draft, publish it as the next version; buyers and merchants read the exact text they accepted."
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <ListBody
                query={query}
                columns={columns}
                rowKey={(p) => p.kind}
                onRow={(p) => navigate(`/policies/${p.kind}`)}
            />
        </div>
    );
}
